import { useState, type ReactNode } from 'react';
import { Download, ExternalLink, Loader2 } from 'lucide-react';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { CharacterCounter } from '@/components/ui/character-counter';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { cleanWhyItWorks, definitionFileName, restoredToastText } from '@/lib/template-admin';
import {
  GOAL_LABELS,
  NICHE_LABELS,
  TEMPLATE_GOALS,
  TEMPLATE_LIMITS,
  TEMPLATE_NICHES,
} from '@/lib/template-labels';
import { toastIfSilent } from '@/lib/template-toast';
import {
  formTemplatesService,
  type AdminTemplateDetail,
  type TemplateGoal,
  type TemplateNiche,
  type TemplatePatch,
  type TemplateStatus,
} from '@/services/formTemplates';
import toastService from '@/services/toast/service';
import { GatedButton } from './GatedButton';
import { PublishVersionDialog } from './PublishVersionDialog';
import { TemplateCoverCard } from './TemplateCoverCard';

interface TemplateOverviewTabProps {
  detail: AdminTemplateDetail;
  /** Refetches the detail and returns it (null when it could not be loaded). */
  onChanged: () => Promise<AdminTemplateDetail | null>;
  /** Opens the studio form's builder in a new tab. */
  onOpenBuilder: () => Promise<void>;
  /** Reloads the page and toasts after a version was published. */
  onPublished: (versionNo: number) => Promise<void>;
}

const STUDIO_FORM_MISSING = "The template's studio form no longer exists";
const NO_VERSION = 'Publish a version first';
const UNSAVED_CHANGES = 'Save or undo your catalogue changes first';

const dateFormat = new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });

/** Saves the text as a file through a temporary link. */
function downloadJson(filename: string, text: string) {
  const url = URL.createObjectURL(new Blob([text], { type: 'application/json' }));
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  // After the click has been handled: some browsers cancel the download when the URL is revoked at once.
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

type StatusAction = 'unpublish' | 'archive' | 'republish';

// The name goes in the body in bold, not in the title: a long name would run under the dialog's close button.
export const STATUS_COPY: Record<
  StatusAction,
  { title: string; body: (name: string) => ReactNode; busy: string; toast: string; to: TemplateStatus; button: string }
> = {
  unpublish: {
    title: 'Unpublish template?',
    body: (name) => (
      <>
        Customers stop seeing <strong>{name}</strong> in the library. Copies they already made don't change. Put it back
        in the library to bring it back.
      </>
    ),
    busy: 'Unpublishing...',
    toast: 'Template unpublished',
    to: 'draft',
    button: 'Unpublish',
  },
  republish: {
    title: 'Put back in the library?',
    body: (name) => (
      <>
        Customers see <strong>{name}</strong> in the library again, on its current version. Copies they already made
        don't change.
      </>
    ),
    busy: 'Restoring...',
    toast: 'Template is back in the library',
    to: 'published',
    button: 'Put back in the library',
  },
  archive: {
    title: 'Archive template?',
    body: (name) => (
      <>
        Customers stop seeing <strong>{name}</strong> in the library. Copies they already made don't change. You can
        restore it later.
      </>
    ),
    busy: 'Archiving...',
    toast: 'Template archived',
    to: 'archived',
    button: 'Archive',
  },
};

/** The "why it works" lines as three fixed inputs. */
function paddedLines(lines: string[]): string[] {
  return Array.from({ length: TEMPLATE_LIMITS.whyLines }, (_, i) => lines[i] ?? '');
}

export function TemplateOverviewTab({ detail, onChanged, onOpenBuilder, onPublished }: TemplateOverviewTabProps) {
  // An older back end sends no booking label.
  const savedBookingLabel = detail.bookingLabel ?? '';
  const [name, setName] = useState(detail.name);
  const [whoLine, setWhoLine] = useState(detail.whoLine);
  const [bookingLabel, setBookingLabel] = useState(savedBookingLabel);
  const [description, setDescription] = useState(detail.description);
  const [niche, setNiche] = useState<TemplateNiche>(detail.niche);
  const [goal, setGoal] = useState<TemplateGoal>(detail.goal);
  const [lines, setLines] = useState(() => paddedLines(detail.whyItWorks));
  const [sortOrder, setSortOrder] = useState(String(detail.sortOrder));
  const [evidence, setEvidence] = useState(detail.evidence ?? '');
  const [saving, setSaving] = useState(false);
  // The action the confirm asks about stays set after it closes, so its text and busy label
  // don't empty while it fades out.
  const [confirm, setConfirm] = useState<StatusAction | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [statusBusy, setStatusBusy] = useState(false);
  const [coverBusy, setCoverBusy] = useState(false);
  const [publishOpen, setPublishOpen] = useState(false);
  const [exporting, setExporting] = useState(false);

  const sortOrderNumber = Number(sortOrder);
  const sortOrderValid = sortOrder.trim() !== '' && Number.isInteger(sortOrderNumber) && sortOrderNumber >= 0;
  const cleanedLines = cleanWhyItWorks(lines);

  // Only the fields that differ from what the page loaded are sent.
  const patch: TemplatePatch = {};
  if (name.trim() !== detail.name) patch.name = name.trim();
  if (whoLine.trim() !== detail.whoLine) patch.whoLine = whoLine.trim();
  if (bookingLabel.trim() !== savedBookingLabel) patch.bookingLabel = bookingLabel.trim();
  if (description.trim() !== detail.description) patch.description = description.trim();
  if (niche !== detail.niche) patch.niche = niche;
  if (goal !== detail.goal) patch.goal = goal;
  if (cleanedLines.join('\n') !== detail.whyItWorks.join('\n')) patch.whyItWorks = cleanedLines;
  if (sortOrderValid && sortOrderNumber !== detail.sortOrder) patch.sortOrder = sortOrderNumber;
  if (evidence.trim() !== (detail.evidence ?? '')) patch.evidence = evidence.trim() === '' ? null : evidence.trim();

  const busy = saving || statusBusy || coverBusy;
  const nameMissing = name.trim() === '';
  // What Save would send: something changed, and every field is valid.
  const hasValidChanges = Object.keys(patch).length > 0 && !nameMissing && sortOrderValid;
  // Unsaved edits (an invalid sort order counts: it is typed but can't be sent) are lost when the tab remounts
  // after a publish or status change, so those wait for Save.
  const hasUnsavedChanges = Object.keys(patch).length > 0 || !sortOrderValid;
  const unsavedReason = hasUnsavedChanges ? UNSAVED_CHANGES : null;
  const cannotSave = busy || !hasValidChanges;

  const handleSave = async () => {
    if (cannotSave) return;
    setSaving(true);
    try {
      await formTemplatesService.update(detail.publicId, patch);
      toastService.success('Template saved');
      await onChanged();
    } catch (err) {
      // A server message was already toasted by the api client; the form keeps what was typed.
      toastIfSilent(err, "Couldn't save the template");
    } finally {
      setSaving(false);
    }
  };

  const askToConfirm = (action: StatusAction) => {
    setConfirm(action);
    setConfirmOpen(true);
  };

  const changeStatus = async (to: TemplateStatus, message: string) => {
    if (busy) return;
    setStatusBusy(true);
    try {
      await formTemplatesService.update(detail.publicId, { status: to });
      toastService.success(message);
      setConfirmOpen(false);
      await onChanged();
    } catch (err) {
      // A server message was already toasted by the api client.
      toastIfSilent(err, "Couldn't change the status");
    } finally {
      setStatusBusy(false);
    }
  };

  const handleExport = async () => {
    if (exporting || detail.currentVersionNo === null) return;
    setExporting(true);
    try {
      const definition = await formTemplatesService.exportVersion(detail.publicId, detail.currentVersionNo);
      downloadJson(definitionFileName(detail.name, detail.currentVersionNo), JSON.stringify(definition, null, 2));
    } catch (err) {
      // A server message was already toasted by the api client.
      toastIfSilent(err, "Couldn't export JSON");
    } finally {
      setExporting(false);
    }
  };

  const studioMissing = detail.studioFormPublicId === null ? STUDIO_FORM_MISSING : null;
  const confirmCopy = confirm === null ? null : STATUS_COPY[confirm];

  return (
    <div className="space-y-4 sm:space-y-6">
      <Card className="py-0 gap-0">
        <CardHeader className="px-5 py-4 gap-0 border-b">
          <CardTitle className="text-base font-semibold">Catalogue</CardTitle>
          <CardDescription>What customers see in the library</CardDescription>
        </CardHeader>
        <CardContent className="p-5 space-y-4">
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label htmlFor="template-name">Name</Label>
              <CharacterCounter current={name.length} max={TEMPLATE_LIMITS.name} />
            </div>
            <Input
              id="template-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={TEMPLATE_LIMITS.name}
              placeholder="Roofing quote request"
              aria-invalid={nameMissing}
            />
            {nameMissing ? (
              <p className="text-xs text-destructive">Add a name</p>
            ) : (
              <p className="text-xs text-muted-foreground">
                What customers see on the card. Name the result, e.g. "Roofing quote request".
              </p>
            )}
          </div>
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label htmlFor="template-who-line">Who it's for</Label>
              <CharacterCounter current={whoLine.length} max={TEMPLATE_LIMITS.whoLine} />
            </div>
            <Input
              id="template-who-line"
              value={whoLine}
              onChange={(e) => setWhoLine(e.target.value)}
              maxLength={TEMPLATE_LIMITS.whoLine}
              placeholder="Remodelers"
            />
            <p className="text-xs text-muted-foreground">Under the name on every template card, in the buyer's words.</p>
          </div>
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label htmlFor="template-booking-label">Booking label</Label>
              <CharacterCounter current={bookingLabel.length} max={TEMPLATE_LIMITS.bookingLabel} />
            </div>
            <Input
              id="template-booking-label"
              value={bookingLabel}
              onChange={(e) => setBookingLabel(e.target.value)}
              maxLength={TEMPLATE_LIMITS.bookingLabel}
              placeholder="Growth call"
            />
            <p className="text-xs text-muted-foreground">
              What a qualified lead books, shown on the card's picture. Leave empty for no pill.
            </p>
          </div>
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label htmlFor="template-description">Description</Label>
              <CharacterCounter current={description.length} max={TEMPLATE_LIMITS.description} />
            </div>
            <Input
              id="template-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              maxLength={TEMPLATE_LIMITS.description}
            />
            <p className="text-xs text-muted-foreground">One line on what the form does.</p>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="template-niche">Niche</Label>
              <Select value={niche} onValueChange={(v) => setNiche(v as TemplateNiche)}>
                <SelectTrigger id="template-niche" className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {TEMPLATE_NICHES.map((n) => (
                    <SelectItem key={n} value={n}>
                      {NICHE_LABELS[n]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">Its category and shelf on the Templates page. Other shows it under More templates.</p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="template-goal">Goal</Label>
              <Select value={goal} onValueChange={(v) => setGoal(v as TemplateGoal)}>
                <SelectTrigger id="template-goal" className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {TEMPLATE_GOALS.map((g) => (
                    <SelectItem key={g} value={g}>
                      {GOAL_LABELS[g]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="template-why-0">Why it works</Label>
            <div className="space-y-2">
              {lines.map((line, i) => (
                <div key={i} className="space-y-1">
                  <div className="flex justify-end">
                    <CharacterCounter current={line.length} max={TEMPLATE_LIMITS.whyLine} />
                  </div>
                  <Input
                    id={`template-why-${i}`}
                    aria-label={`Why it works, line ${i + 1}`}
                    value={line}
                    onChange={(e) => setLines((prev) => prev.map((l, j) => (j === i ? e.target.value : l)))}
                    maxLength={TEMPLATE_LIMITS.whyLine}
                  />
                </div>
              ))}
            </div>
            <p className="text-xs text-muted-foreground">
              Up to 3 lines, benefit first. Shown in the New Form dialog's template detail.
            </p>
          </div>
          <div className="space-y-2 sm:max-w-[200px]">
            <Label htmlFor="template-sort-order">Sort order</Label>
            <Input
              id="template-sort-order"
              type="number"
              min={0}
              step={1}
              value={sortOrder}
              onChange={(e) => setSortOrder(e.target.value)}
              aria-invalid={!sortOrderValid}
            />
            {sortOrderValid ? (
              <p className="text-xs text-muted-foreground">Lower comes first in the library.</p>
            ) : (
              <p className="text-xs text-destructive">Enter a whole number, 0 or more</p>
            )}
          </div>
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label htmlFor="template-evidence">Evidence (internal)</Label>
              <CharacterCounter current={evidence.length} max={TEMPLATE_LIMITS.evidence} />
            </div>
            <Textarea
              id="template-evidence"
              value={evidence}
              onChange={(e) => setEvidence(e.target.value)}
              maxLength={TEMPLATE_LIMITS.evidence}
              rows={4}
            />
            <p className="text-xs text-muted-foreground">The pattern behind this template. Never shown to customers.</p>
          </div>
        </CardContent>
        <CardFooter className="px-5 py-4 border-t justify-end gap-4">
          {hasValidChanges && <p className="mr-auto text-xs text-muted-foreground">You have unsaved changes</p>}
          <GatedButton onClick={handleSave} disabled={cannotSave}>
            {saving ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Saving...
              </>
            ) : (
              'Save'
            )}
          </GatedButton>
        </CardFooter>
      </Card>

      <TemplateCoverCard
        publicId={detail.publicId}
        coverUrl={detail.coverUrl ?? null}
        blockedReason={unsavedReason}
        busy={busy}
        onBusyChange={setCoverBusy}
        onChanged={onChanged}
      />

      <Card className="py-0 gap-0">
        <CardHeader className="px-5 py-4 gap-0 border-b">
          <CardTitle className="text-base font-semibold">Studio</CardTitle>
          <CardDescription>
            Edit the questions in the normal builder, then publish a version. Customers always get the latest
            published version.
          </CardDescription>
        </CardHeader>
        <CardContent className="p-5 space-y-4">
          <div className="flex flex-wrap items-center gap-2">
            <GatedButton variant="outline" onClick={() => void onOpenBuilder()} blockedReason={studioMissing}>
              <ExternalLink className="mr-2 h-4 w-4" />
              Open in builder
            </GatedButton>
            <GatedButton
              write={false}
              variant="outline"
              onClick={handleExport}
              disabled={exporting}
              blockedReason={detail.currentVersionNo === null ? NO_VERSION : null}
            >
              <Download className={`mr-2 h-4 w-4 ${exporting ? 'animate-pulse' : ''}`} />
              {exporting ? 'Exporting…' : 'Export JSON'}
            </GatedButton>
            <GatedButton onClick={() => setPublishOpen(true)} blockedReason={studioMissing ?? unsavedReason}>
              Publish new version
            </GatedButton>
          </div>
          <div className="space-y-2">
            <h3 className="text-xs font-medium text-muted-foreground">Versions</h3>
            {detail.versions.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                No version yet. Publish one to put this template in the library.
              </p>
            ) : (
              <ul className="space-y-2">
                {detail.versions.map((version) => (
                  <li
                    key={version.versionNo}
                    className="flex flex-wrap items-center gap-x-3 gap-y-1 p-3 rounded-lg bg-muted/50 text-sm"
                  >
                    <span className="font-medium">{`v${version.versionNo}`}</span>
                    {version.isCurrent && detail.status === 'published' && <Badge tone="success">Live</Badge>}
                    <span className="text-muted-foreground">{dateFormat.format(new Date(version.publishedAtUtc))}</span>
                    {version.publishedBy && (
                      <span className="text-muted-foreground">{`by ${version.publishedBy.name}`}</span>
                    )}
                    {version.changeNote && <span className="min-w-0 basis-full sm:basis-auto">{version.changeNote}</span>}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </CardContent>
      </Card>

      <Card className="py-0 gap-0">
        <CardHeader className="px-5 py-4 gap-0 border-b">
          <CardTitle className="text-base font-semibold">Status</CardTitle>
          <CardDescription>Whether customers can use this template</CardDescription>
        </CardHeader>
        <CardContent className="p-5 flex flex-wrap items-center gap-2">
          {detail.status === 'published' && (
            <GatedButton
              variant="outline"
              onClick={() => askToConfirm('unpublish')}
              disabled={busy}
              blockedReason={unsavedReason}
            >
              Unpublish
            </GatedButton>
          )}
          {detail.status === 'draft' && detail.currentVersionNo !== null && (
            <GatedButton
              variant="outline"
              onClick={() => askToConfirm('republish')}
              disabled={busy}
              blockedReason={unsavedReason}
            >
              Put back in the library
            </GatedButton>
          )}
          {detail.status !== 'archived' && (
            <GatedButton
              variant="outline"
              onClick={() => askToConfirm('archive')}
              disabled={busy}
              blockedReason={unsavedReason}
            >
              Archive
            </GatedButton>
          )}
          {detail.status === 'archived' && (
            <GatedButton
              variant="outline"
              onClick={() => changeStatus('draft', restoredToastText(detail.currentVersionNo !== null))}
              disabled={busy}
              blockedReason={unsavedReason}
            >
              Restore
            </GatedButton>
          )}
        </CardContent>
      </Card>

      <PublishVersionDialog
        open={publishOpen}
        onOpenChange={setPublishOpen}
        detail={detail}
        onPublished={onPublished}
        onUnanswered={onChanged}
        onOpenBuilder={onOpenBuilder}
      />

      <AlertDialog open={confirmOpen} onOpenChange={(open) => !open && !statusBusy && setConfirmOpen(false)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{confirmCopy?.title}</AlertDialogTitle>
            <AlertDialogDescription>{confirmCopy?.body(detail.name)}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={statusBusy}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                // Not while the dialog fades out: a click then must not repeat the change.
                if (confirmOpen && confirmCopy !== null) void changeStatus(confirmCopy.to, confirmCopy.toast);
              }}
              disabled={statusBusy}
            >
              {statusBusy ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {confirmCopy?.busy}
                </>
              ) : (
                confirmCopy?.button
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
